import React from "react";
import { Tag } from "antd";
import { PageHeader, PageSection } from "@dowty/ui";
import { useTranslation } from "react-i18next";
import "./GamePage.css";

type GamePageProps = {
    game: string;
    tagColor: string;
    instructionIcons: React.ReactNode[];
    fallbackInstructionIcon?: React.ReactNode;
    children?: React.ReactNode;
    maxWidth?: number | string;
};

const GamePage: React.FC<GamePageProps> = ({
    game,
    tagColor,
    instructionIcons,
    fallbackInstructionIcon = instructionIcons[0],
    children,
    maxWidth = 1040,
}) => {
    const { t } = useTranslation();
    const key = (name: string) => `games.details.${game}.${name}`;
    const text = (name: string) => {
        const value = t(key(name), { defaultValue: "" });
        return value && value !== key(name) ? value : undefined;
    };
    const items = (name: string) => {
        const value: unknown = t(key(name), { returnObjects: true, defaultValue: [] });
        return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && !!item) : [];
    };
    const title = text("title");
    const tagline = text("tagline");
    const description = text("description");
    const requirementsTitle = text("requirementsTitle");
    const requirements = items("requirements");
    const instructionsTitle = text("instructionsTitle");
    const instructions = items("instructions");
    const summary = text("summary");

    return (
        <section className="game-page" style={{ maxWidth }}>
            {title && (
                <header className="game-page__hero">
                    <PageHeader
                        title={title}
                        subtitle={description}
                        extra={tagline && <Tag className="game-page__tag" color={tagColor}>{tagline}</Tag>}
                    />
                </header>
            )}

            {children && <section className="game-page__play" aria-label={title || undefined}>{children}</section>}

            {(requirements.length > 0 || instructions.length > 0) && (
                <section className="game-page__rules" aria-label={instructionsTitle || requirementsTitle || undefined}>
                    {requirements.length > 0 && (
                        <PageSection title={requirementsTitle ?? ""}>
                            <ul className="game-page__requirements" aria-label={requirementsTitle || undefined}>
                                {requirements.map((requirement, index) => <li key={`${requirement}-${index}`}>{requirement}</li>)}
                            </ul>
                        </PageSection>
                    )}

                    {instructions.length > 0 && (
                        <PageSection title={instructionsTitle ?? ""}>
                            <ol className="game-page__instructions" aria-label={instructionsTitle || undefined}>
                                {instructions.map((step, index) => (
                                    <li key={`${step}-${index}`}>
                                        <span className="game-page__step-number">{index + 1}</span>
                                        <span className="game-page__step-icon" aria-hidden="true">
                                            {instructionIcons[index] ?? fallbackInstructionIcon}
                                        </span>
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ol>
                        </PageSection>
                    )}
                </section>
            )}

            {summary && <p className="game-page__summary">{summary}</p>}
        </section>
    );
};

export default GamePage;
